// ./src/components/features/EventTickets/TicketChangeTypeDrawer/TicketChangeTypeDrawer.tsx
//
// Move an issued ticket to a different ticket type (GA → VIP, …). The server
// availability-checks the destination type (no overselling) and re-snapshots the
// price. Loads the event's on-sale types; the current type is pre-selected.
//
// ROADMAP #120 (2026-10-08): this was a modal opened from `useState`; it is now a drawer (house rule: AppDrawer, not a
// modal) on its own URL stage (`…/:id/change-type`), so a reload or a phone resumed from the background reopens it.
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Stack, Text, Loader, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { EventTicket, TicketType, TicketTypeService, listAllPages } from "@/utils/api";
import { AppDrawer, SelectField } from "@/components/generic";
import { formatErrors, getFormErrors } from "@/utils";
import { useFormSnapshot, useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";

// The refusals that matter here are all named `ticketTypeId` by the server ("sold out",
// "sold for a different date", "Ticket type not found") — they belong on the type picker.
const KNOWN_FORM_FIELDS = ["ticketTypeId"] as const;

export interface TicketChangeTypeDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  ticket: EventTicket | null;
  /** Runs the change; resolves with the OpResult so the drawer can surface errors. */
  onConfirm: (ticketTypeId: string) => Promise<OpResult>;
  inProgress?: boolean;
}

const fmtPrice = (tt: TicketType): string => {
  if (tt.price == null) {
    return "";
  }

  const n = Number(tt.price);
  if (!Number.isFinite(n)) {
    return "";
  }

  const v = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return tt.currencyCode ? ` · ${tt.currencyCode} ${v}` : ` · ${v}`;
};

const TicketChangeTypeDrawerComponent: React.FC<TicketChangeTypeDrawerProps> = ({
  opened,
  onClose,
  eventId,
  ticket,
  onConfirm,
  inProgress = false,
}) => {
  const { t } = useTranslation(["ticketSales", "common"]);

  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<TicketType[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<OpResult | null>(null);

  const { isDirty, seedFrom, clear } = useFormSnapshot({ selected });

  const ticketId = ticket?.id ?? null;
  const currentTypeId = ticket?.ticketTypeId ?? null;
  const currentTypeRef = useRef(currentTypeId);
  currentTypeRef.current = currentTypeId;

  // Seeded once per TICKET, keyed on its id: the page patching the row (a new object for the same ticket) must not put
  // the picker back while the host is choosing.
  useEffect(() => {
    if (!opened || !ticketId) {
      clear();

      return undefined;
    }

    setError(null);
    setSelected(currentTypeRef.current);
    seedFrom({ selected: currentTypeRef.current });
    setLoading(true);

    let cancelled = false;

    listAllPages(TicketTypeService.list, { eventId: [eventId], isAddOn: false }).then((res) => {
      if (cancelled) {
        return;
      }

      if (res.code === OP_RESULT_CODES.OK && Array.isArray(res.data)) {
        setTypes(res.data as TicketType[]);
      } else {
        setError(res);
      }

      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [opened, eventId, ticketId, clear, seedFrom]);

  const options = useMemo(
    () =>
      types
        .filter((tt) => tt.id)
        .map((tt) => ({ value: tt.id as string, label: `${tt.name || "—"}${fmtPrice(tt)}` })),
    [types]
  );

  const unchanged = !selected || selected === currentTypeId;
  const canConfirm = !loading && !unchanged && !inProgress;

  const handleConfirm = useCallback(async () => {
    if (!canConfirm || !selected) {
      return;
    }

    setError(null);

    const result = await onConfirm(selected);

    if (result.code === OP_RESULT_CODES.OK) {
      onClose();

      return;
    }

    setError(result);
  }, [canConfirm, selected, onConfirm, onClose]);

  // ⌘S / ⌘Enter only. Esc is AppDrawer's own — it honours `closable` (not while moving the ticket).
  useModalShortcuts({ onConfirm: handleConfirm, canConfirm, enabled: opened });

  const { fields: fieldErrors, genericErrors } = useMemo(() => getFormErrors(error, KNOWN_FORM_FIELDS), [error]);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("ticketSales:changeType.title", { defaultValue: "Change ticket type" })}
      position="right"
      size="md"
      mobileFitContent
      closable={!inProgress}
      loading={inProgress}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="confirm-cancel"
      onConfirm={handleConfirm}
      confirmLabel={t("ticketSales:changeType.confirm", { defaultValue: "Move ticket" })}
      confirmIcon="Ticket"
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={!canConfirm}
      isDirty={isDirty}
    >
      <Stack gap="md">
        {ticket?.ticketCode && (
          <Text size="md" c="dimmed">
            {t("ticketSales:changeType.ticket", { defaultValue: "Ticket {{code}}", code: ticket.ticketCode })}
          </Text>
        )}

        {loading ? (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        ) : (
          <SelectField
            label={t("ticketSales:changeType.newType", { defaultValue: "New ticket type" })}
            data={options}
            value={selected}
            onChange={(_name, v) => setSelected(v)}
            searchable
            size="md"
            nothingFoundMessage={t("ticketSales:changeType.noTypes", { defaultValue: "No ticket types" })}
            error={fieldErrors.ticketTypeId}
          />
        )}

        <Text size="md" c="dimmed">
          {t("ticketSales:changeType.hint", {
            defaultValue: "The new type's price is applied and availability is checked — a sold-out type is rejected.",
          })}
        </Text>
      </Stack>
    </AppDrawer>
  );
};

export const TicketChangeTypeDrawer = React.memo(TicketChangeTypeDrawerComponent);
